import { ActionIcon, Badge, Grid, useMantineTheme } from '@mantine/core'
import { networkStore } from 'App/ServiceCenter/store/networkStore'
import { Button } from 'components/Button/Button'
import { IconHorizontalDotMenu } from 'components/icons/IconHorizontalDotMenu'
import type { Point } from 'geojson'
import { translate } from 'i18n/i18n'
import React from 'react'
import { colors } from 'settings/colors'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { Authorization } from '../../../../../components/Authorization/Authorization'
import { useTimezoneForSelectedPropertyOrRegion } from '../../../../Map/PanelDetails/_utils/useTimezoneForSelectedPropertyOrRegion'
import { openActionMenu } from '../../../ActionMenu/ActionMenu'
import { getEquipmentTypeLabel } from '../../../Map/_utils/equipmentTypes'
import { getNodeStatusLabel, NODE_STATUS_COLORS } from '../../../Map/_utils/getActiveNodeStatus'
import { getNodeEquipmentType } from '../../../Map/_utils/getNodeEquipmentType'
import { useNodeSelect } from '../../../Map/_utils/useNodeSelect'
import { openNodeMaintenanceModal } from '../../../NodeMaintenanceModal/NodeMaintenanceModal'
import { DnNodeLog } from '../../../SelectedEquipmentCard/ActiveNodeDetail/log/DnNodeLog'
import { LnNodeLog } from '../../../SelectedEquipmentCard/ActiveNodeDetail/log/LnNodeLog'
import { SnNodeLog } from '../../../SelectedEquipmentCard/ActiveNodeDetail/log/SnNodeLog'
import type {
  TActiveGateway,
  TActiveNode,
  TDnNodeLog,
  TLnNodeLog,
  TNodeLog,
  TPlannedGateway,
  TPlannedNode,
  TSnNodeLog,
} from '../../../types'
import { TNodeStatus } from '../../../types'
import { getIdentifier } from '../../../utils/getIdentifier'
import { getNodeActions } from '../../../utils/getNodeActions'
import { getNodeNetworkType } from '../../../utils/getNodeNetworkType'
import { isPlannedNode } from '../../../utils/isPlannedNode'
import { DevicesLogs } from '../Devices/DevicesLogs'
import { OnboardLogs } from '../Devices/OnboardLogs'

type NodeRowProps = {
  node: TActiveNode | TActiveGateway | TPlannedNode | TPlannedGateway
  status: TNodeStatus
  lastLog?: TNodeLog
}

export const NodeRow: React.FC<NodeRowProps> = ({ node, status, lastLog }) => {
  const theme = useMantineTheme()
  const { selectNode } = useNodeSelect()
  const isWideScreen = useIsWideScreen()
  const nodeStatusText = getNodeStatusLabel(status)
  const nodeStatusColor = NODE_STATUS_COLORS[status]
  const nodeType = (node as TActiveNode).nodeType || (node as TActiveGateway).gatewayType
  const propertyId = (node as TActiveNode).propertyId || (node as TActiveGateway).propertyIds[0]
  const networkType = getNodeNetworkType(node)
  const nodeTypeText = nodeType.toUpperCase().replace('_', '-')
  const equipmentType = getNodeEquipmentType(nodeType)
  const equipmentTypeText = getEquipmentTypeLabel(equipmentType)
  const hasServiceIssue = status === TNodeStatus.ERROR
  const timezone = useTimezoneForSelectedPropertyOrRegion()
  const connected = networkStore.useSelector((s) => s.connectionStatus.connected)

  const narrowScreenStyles = {
    'dt': {
      width: 'auto !important',
      marginRight: 5,
      lineHeight: 2,
    },
    'dt::after': {
      content: '":"',
    },
    'dd': {
      width: 'auto !important',
      marginRight: 20,
      lineHeight: 2,
    },
    '.SignalStrength': {
      top: 3,
    },
  }

  const wideScreenStyles = {
    'dt': {
      display: 'block !important',
      width: 'auto !important',
      lineHeight: 1,
    },
    'dd': {
      display: 'block !important',
      width: 'auto !important',
      lineHeight: 2,
    },
    '.SignalStrength': {
      top: 5,
    },
  }

  const handleOpenActionMenu = () => {
    const actions = getNodeActions(node, connected)

    openActionMenu(actions)
  }

  const handleSelectNode = () => {
    let nodeId

    if (isPlannedNode(node)) {
      nodeId = (node as TPlannedNode).id
    } else {
      nodeId = getIdentifier(node as TActiveNode)
    }

    const coords = JSON.parse(node.location as string) as Point

    selectNode(nodeId, status, coords)
  }

  const nodeDevices = (node as TActiveNode).devices

  return (
    <div css={{ borderBottom: `1px solid ${theme.colors.grey[1]}`, padding: '15px 0' }}>
      <div
        className="header"
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isWideScreen ? 'center' : 'flex-start',
        }}
      >
        <div className="left" css={{ flex: '1 1 auto' }}>
          <h3 css={{ margin: 0, cursor: 'pointer' }} onClick={handleSelectNode}>
            {node.name || equipmentTypeText}
            <Badge
              css={{
                color: theme.colors.midnight,
                backgroundColor: nodeStatusColor,
                marginLeft: 10,
                verticalAlign: 'middle',
              }}
            >
              {nodeStatusText}
            </Badge>
          </h3>

          {!isPlannedNode(node) && (
            <div css={{ fontSize: '10px', fontWeight: 500, marginTop: '3px', marginBottom: '2px' }}>
              {(node as TActiveNode).nodeIdentifier || (node as TActiveGateway).gatewayIdentifier}
            </div>
          )}

          <div css={{ fontSize: '10px', fontWeight: 500 }}>{nodeTypeText}</div>

          {!isPlannedNode(node) && (
            <Authorization requires={{ permission: 'VIEW_SSC_NODE_LAST_LOG', entity: propertyId }}>
              <div
                css={{
                  'fontSize': '12px',
                  'marginTop': 10,
                  'dl': {
                    display: 'flex',
                    flexDirection: isWideScreen ? 'row' : 'column',
                  },
                  '.row': {
                    marginRight: 20,
                  },
                  ...(isWideScreen ? wideScreenStyles : narrowScreenStyles),
                }}
              >
                <Grid css={{ fontSize: '14px' }}>
                  {networkType === 'SN' && (
                    <SnNodeLog log={lastLog as TSnNodeLog} nodeType={nodeType} timezone={timezone} />
                  )}

                  {!!lastLog && networkType === 'DN' && (
                    <DnNodeLog log={lastLog as TDnNodeLog} nodeType={nodeType} timezone={timezone} />
                  )}

                  {!!lastLog && networkType === 'LN' && (
                    <LnNodeLog log={lastLog as TLnNodeLog} nodeType={nodeType} timezone={timezone} />
                  )}
                </Grid>
              </div>
            </Authorization>
          )}
        </div>

        <div className="right" css={{ display: 'flex', alignItems: 'center' }}>
          {isWideScreen && hasServiceIssue && (
            <Button variant="tertiary" onClick={() => openNodeMaintenanceModal(node as TActiveNode)}>
              {translate.phrases.placeholder('Maintenance')}
            </Button>
          )}

          <ActionIcon color={colors.primary} css={{ marginLeft: 20 }} onClick={handleOpenActionMenu}>
            <span css={{ transform: 'translateX(2px) rotate(90deg)', fontSize: '22px' }}>
              <IconHorizontalDotMenu />
            </span>
          </ActionIcon>
        </div>
      </div>

      {!isWideScreen && hasServiceIssue && (
        <Button
          variant="tertiary"
          css={{ marginTop: 5 }}
          fullWidth
          size="lg"
          onClick={() => openNodeMaintenanceModal(node as TActiveNode)}
        >
          {translate.phrases.placeholder('Maintenance')}
        </Button>
      )}

      {!!nodeDevices && (
        <DevicesLogs
          configuredDevices={nodeDevices}
          deviceLogs={(lastLog as TSnNodeLog)?.devices}
          nodeType={nodeType}
        />
      )}

      {nodeType === 'ln_r' && (lastLog as TSnNodeLog)?.onboard && (
        <OnboardLogs onboardLogs={(lastLog as TSnNodeLog)?.onboard} />
      )}
    </div>
  )
}
